import type { DocPage } from '../types'
import { media } from '../media'

export const gettingStarted: DocPage = {
  id: 'getting-started',
  title: 'Getting started',
  description:
    'Launch the app, wait for backends, sign in, configure your first Source/Target, save Model Configurations and Transfer Profiles, and run your first transfer.',
  category: 'Getting started',
  sections: [
    { id: 'launch', title: 'Launch and splash' },
    { id: 'signin', title: 'Sign in briefly' },
    { id: 'first-transfer', title: 'Your first transfer' },
    { id: 'configs-profiles', title: 'Configs and profiles early' },
    { id: 'language-theme', title: 'Language and theme' },
    { id: 'next', title: 'Where to go next' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'launch',
      text: 'Launch and splash',
    },
    {
      type: 'steps',
      items: [
        'Start SEPM X-Translator from your installer shortcut or start menu entry.',
        'On the splash screen, wait for “Starting Python backend…”, “Starting CAD backend…”, and “Backends are ready…”.',
        'When the login card appears, sign in with your SEPM account (see Account & subscription).',
        'If prompted, switch to an organization that has X-Translator access.',
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Session restore',
      text: 'If you signed in recently, X-Translator may restore your session automatically (“Checking session…”). If restore fails, the login card appears again.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'signin',
      text: 'Sign in briefly',
    },
    {
      type: 'paragraph',
      text: 'Use Sign In on the login card (or Sign in using system browser instead). Complete SEPM login until you see Sign-in successful, then return to the app. Details, organizations, and entitlements are covered under Account & subscription.',
    },
    {
      type: 'figure',
      src: media.signInScreen1,
      alt: 'X-Translator login card',
      caption: 'Figure: X-Translator login card — start Sign In here.',
      size: 'compact',
    },
    {
      type: 'figure',
      src: media.signInSuccessful,
      alt: 'Sign-in successful',
      caption: 'Figure: Sign-in successful — return to the desktop app.',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'first-transfer',
      text: 'Your first transfer',
    },
    {
      type: 'steps',
      items: [
        'In the Source panel, choose Select Format → New and configure a connection (for example PostGIS or a GeoPackage file).',
        'Optionally add Filters, then proceed so the model tree loads.',
        'If entitled, open Create Custom Config / Save Config and give the Source setup a Configuration Name you will recognize later.',
        'In the Target panel, choose a target format and configure its connection or scratch output path; save a Target config the same way when useful.',
        'Select a source collection and a target collection, then use Connect (or open Auto Map).',
        'Map attributes as needed; add fixed or value mappings if required.',
        'Click Transfer, review Transfer Review (formats, MAPPED/SCRATCH, coordinates, spatial filter, hidden layers), and watch progress in the bottom logger.',
        'Optionally save a Transfer Profile so you can reload Source + Target + Mapping together next time.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'configs-profiles',
      text: 'Configs and profiles early',
    },
    {
      type: 'paragraph',
      text: 'New users often re-enter connection strings for every session. Avoid that by treating Model Configurations and Transfer Profiles as part of the first-run habit:',
    },
    {
      type: 'table',
      headers: ['Artifact', 'What it stores', 'When to create'],
      rows: [
        [
          'Model Configuration',
          'One side’s format connection, filters, and format options',
          'As soon as a Source or Target connects cleanly',
        ],
        [
          'Transfer Profile',
          'Source config + Target config + mapping (+ related settings)',
          'After your first successful mapped/scratch setup you want to reuse',
        ],
        [
          'Default config',
          'Pre-fills New connections for a format',
          'When one connection is your everyday baseline',
        ],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Permission note',
      text: 'Create Custom Config requires the create_custom_config entitlement. Transfer History requires view-transfer-history. If those actions are locked, you can still run transfers with New each time — ask an admin to unlock saving for your organization.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'language-theme',
      text: 'Language and theme',
    },
    {
      type: 'list',
      items: [
        'Language — switch the UI between English and German from the header.',
        'Theme — toggle light or dark appearance from the header.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'next',
      text: 'Where to go next',
    },
    {
      type: 'list',
      items: [
        'Workspace tour — learn every chrome area.',
        'Model configurations — save/load/default/unsaved behavior.',
        'Mapping overview — Connect, Auto Map, fixed/value mappings.',
        'Running transfers — Transfer Review and logger.',
        'Formats overview — pick the deep chapter for your data store.',
      ],
    },
  ],
}
